import { useState } from 'react'
import { router } from '@inertiajs/react'
import { PersonDetail, roleOptionsForAssignment, UserRole } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  user: PersonDetail
  managers: { id: number; name: string }[]
  /** Roles the current viewer is allowed to hand out (server-enforced too). */
  assignableRoles?: string[]
  canSetPassword?: boolean
  onClose: () => void
}

export default function EditPersonModal({ user, managers, assignableRoles = [], canSetPassword = false, onClose }: Props) {
  const [form, setForm] = useState({
    role: user.role as UserRole,
    manager_id: user.manager_id?.toString() || '',
    has_hr_permission: user.has_hr_permission,
    is_executive_leadership: user.is_executive_leadership,
  })
  const [submitting, setSubmitting] = useState(false)

  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [pwSubmitting, setPwSubmitting] = useState(false)
  const [pwErrors, setPwErrors] = useState<{ password?: string; password_confirmation?: string }>({})
  const [pwSuccess, setPwSuccess] = useState(false)

  const handleSetPassword = (e: React.FormEvent) => {
    e.preventDefault()
    setPwSubmitting(true)
    setPwErrors({})
    setPwSuccess(false)
    router.patch(`/people/${user.id}/set_password`, {
      user: { password, password_confirmation: passwordConfirmation },
    }, {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => setPwSubmitting(false),
      onSuccess: () => {
        setPassword('')
        setPasswordConfirmation('')
        setPwSuccess(true)
      },
      onError: (errs) => {
        setPwErrors({
          password: errs.password as string | undefined,
          password_confirmation: errs.password_confirmation as string | undefined,
        })
      },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.put(`/people/${user.id}`, {
      user: {
        role: form.role,
        manager_id: form.manager_id || null,
        has_hr_permission: form.has_hr_permission,
        is_executive_leadership: form.is_executive_leadership,
      },
    }, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => onClose(),
    })
  }

  const updateField = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-elevated w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-stone/20 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary font-heading">Edit Person</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-stone/5 rounded-lg px-4 py-3 border border-stone/15">
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <span className="block text-sm font-medium text-text-muted mb-0.5">Name</span>
                <span className="text-sm text-text-primary">{user.full_name}</span>
              </div>
              <div>
                <span className="block text-sm font-medium text-text-muted mb-0.5">Email</span>
                <span className="text-sm text-text-primary">{user.email}</span>
              </div>
            </div>
            {(user.title || user.phone) && (
              <div className="grid grid-cols-2 gap-3 mb-2">
                {user.title && (
                  <div>
                    <span className="block text-sm font-medium text-text-muted mb-0.5">Title</span>
                    <span className="text-sm text-text-primary">{user.title}</span>
                  </div>
                )}
                {user.phone && (
                  <div>
                    <span className="block text-sm font-medium text-text-muted mb-0.5">Phone</span>
                    <span className="text-sm text-text-primary">{user.phone}</span>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-text-muted mt-1">Profile details are managed by the user from their own profile page.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Role</label>
              <select
                value={form.role}
                onChange={e => updateField('role', e.target.value)}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
              >
                {roleOptionsForAssignment(assignableRoles, user.role as UserRole).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Manager</label>
              <select
                value={form.manager_id}
                onChange={e => updateField('manager_id', e.target.value)}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
              >
                <option value="">No manager</option>
                {sortPeople(managers.filter(m => m.id !== user.id)).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.has_hr_permission}
                onChange={e => updateField('has_hr_permission', e.target.checked)}
                className="w-4 h-4 text-pinnacle-red focus:ring-pinnacle-red/20 border-stone/40 rounded"
              />
              <span className="text-sm text-text-primary">HR Permission</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_executive_leadership}
                onChange={e => updateField('is_executive_leadership', e.target.checked)}
                className="w-4 h-4 text-pinnacle-red focus:ring-pinnacle-red/20 border-stone/40 rounded"
              />
              <span className="text-sm text-text-primary">Executive Leadership</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-primary hover:text-text-primary transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {canSetPassword && (
          <div className="px-6 pb-6 -mt-2">
            <div className="border-t border-stone/20 pt-4">
              {!showPassword ? (
                <button
                  type="button"
                  onClick={() => { setShowPassword(true); setPwSuccess(false) }}
                  className="text-sm font-medium text-pinnacle-red hover:text-pinnacle-red/80 transition-colors"
                >
                  Set password
                </button>
              ) : (
                <form onSubmit={handleSetPassword} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-text-primary">Set Password</h3>
                    <button
                      type="button"
                      onClick={() => { setShowPassword(false); setPassword(''); setPasswordConfirmation(''); setPwErrors({}) }}
                      className="text-xs text-text-muted hover:text-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="text-xs text-text-muted">
                    This changes {user.full_name}'s login password across every organization their account is part of. Share the new password with them directly.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">New password</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                    />
                    {pwErrors.password && (
                      <p className="text-xs text-pinnacle-red mt-1">{pwErrors.password}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Confirm password</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={passwordConfirmation}
                      onChange={e => setPasswordConfirmation(e.target.value)}
                      className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                    />
                    {pwErrors.password_confirmation && (
                      <p className="text-xs text-pinnacle-red mt-1">{pwErrors.password_confirmation}</p>
                    )}
                  </div>
                  {pwSuccess && (
                    <p className="text-xs text-[#15532E] font-medium">Password updated. Share it with {user.full_name} directly.</p>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={pwSubmitting || !password || !passwordConfirmation}
                      className="px-4 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
                    >
                      {pwSubmitting ? 'Setting...' : 'Set Password'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
